import * as React from 'react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  FindWorkflowDocument,
  NewRequestPageDocument,
  ProcurementTypePublishDocument,
  RequestTypeDetailDocument,
  VersioningProcurementTypeDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useLoadForm } from 'modules/forms/hooks/useForms'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { RequestTypeDetailHeader } from 'modules/procurement/components/RequestTypeDetailHeader'
import PublishTypeModal from 'modules/procurement/components/procurementTypes/PublishTypeModal'
import SaveRequestTypeModal from 'modules/procurement/components/procurementTypes/SaveRequestTypeModal'
import { NewPurchaseRequestModal } from 'modules/procurement/components/purchaseRequests/NewRequestModal'
import TabContent from 'modules/procurement/pages/RequestTypeDetailPage/TabContent'
import { WorkflowProvider, useWorkflow } from 'modules/workflows/contexts/WorkflowProvider'

import { generatePreview } from './helpers/generatePreview'
import { getCurrencies } from './helpers/getCurrencies'
import { getFeatureFlags } from './helpers/getFeatureFlags'
import { getWorkflow } from './helpers/getWorkflow'
import { useFormState } from './helpers/useFormState'

function RequestTypeDetailPageContent() {
  /* Page params and session data */
  const { id } = useParams<{ id: string }>()
  const { employee, company } = useCurrentSession()

  /* Modal states */
  const [publishTypeModalOpen, setPublishTypeModalOpen] = useState(false)
  const [saveFormModalOpen, setSaveFormModalOpen] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)

  /* Mutations */

  // Create a new form version. This creates a draft form and version it.
  const [versioningForm, { loading: saveFormLoading }] = useMutation(
    VersioningProcurementTypeDocument,
    {
      extractorFn: (data) => data.procurementMutations.versioningProcurementType,
      refetchQueries: [RequestTypeDetailDocument, FindWorkflowDocument],
    }
  )

  // TODO: Validate if is updating the correct elements.
  // Publish the procurement type. It should be an atomic operation that activates the form, workflow and the procurement type.
  const [publishProcurementType, { loading: publishFormLoading }] = useMutation(
    ProcurementTypePublishDocument,
    {
      extractorFn: (data) => data.procurementMutations.publishProcurementType,
      refetchQueries: [RequestTypeDetailDocument, FindWorkflowDocument],
    }
  )

  /* Queries */
  // Get the currencies for the new purchase request modal (preview)
  const { data: newRequestPageData } = useQuery(NewRequestPageDocument, {
    variables: { companyId: company?.id || 0, enabled: true },
    skip: !company?.id || !showPreviewModal,
  })

  // Get the data for the entire RequestType
  const {
    data: requestTypeDetailData,
    loading,
    error,
  } = useQuery(RequestTypeDetailDocument, {
    variables: {
      requestTypeId: Number(id),
      companyId: company?.id || 0,
    },
    skip: !id || !company?.id,
  })

  // Feature flags check
  const { isCustomFormsEnabled, isWorkflowsEnabled } = getFeatureFlags(requestTypeDetailData)

  // Get the workflow data for the request type
  const { data: workflowsData } = useQuery(FindWorkflowDocument, {
    variables: {
      companyId: company?.id || 0,
      targetIds: [Number(id)],
    },
    skip: !company?.id || !id || !isWorkflowsEnabled,
  })

  // Define the request type to be used in the page and dependencies
  const requestType = requestTypeDetailData?.procurement?.typesConnection?.nodes?.[0]

  /* Data hooks (form and workflow) */

  // Workflow data
  const { isDirty, saveWorkflow } = useWorkflow()
  const { workflow, hasWorkflowChanges, isLastWorkflowVersionPublished } = getWorkflow(
    workflowsData,
    isDirty
  )

  // Form data
  const { form: hookForm } = useLoadForm({
    formId: requestType?.formsConnection?.nodes?.[0]?.id,
    resourceType: 'Procurement::Type',
  })
  const { form, setForm, hasFormChanges, isLastFormVersionPublished, saveForm } = useFormState(
    requestType,
    hookForm
  )

  // Currencies data
  const { currencies, userCurrency } = getCurrencies(newRequestPageData)

  /* Handlers */
  // TODO: The save process should be refactored to be more robust and handle errors better. It should be an atomic operation on the backend.
  const handleSave = async () => {
    try {
      // 1. Save Workflow if needed
      if (workflow && hasWorkflowChanges) {
        await saveWorkflow()
      }

      // 2. Save Form if needed
      if (form && hasFormChanges) {
        // If the form is the last version published, create a new form version
        if (isLastFormVersionPublished && id) {
          const result = await versioningForm({
            variables: {
              id: id.toString(),
              authorId: employee.id,
              formId: form.id,
              companyId: company.id,
            },
          })
          handleResult(result, () => {
            throw new Error('Versioning failed')
          })
        }
        // TODO: Validate if is updating the correct form.
        const result = await saveForm()
        if (!result.success) {
          throw new Error('Form update failed')
        }
      }

      // If the save process is successful, close the modal and show a flash message
      setSaveFormModalOpen(false)
      app.setFlash({
        type: 'correct',
        message: i18n.t('procurement.types.forms.edit_form_message'),
      })
    } catch {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  // TODO: The publish process should be refactored. It should be an atomic operation on the backend that handles the activation of form, workflow and the procurement type.
  const handlePublish = async () => {
    try {
      if (!id) {
        throw new Error('Request type ID is required')
      }

      // Publish the workflow if there are changes
      const result = await publishProcurementType({
        variables: {
          companyId: company.id,
          procurementTypeId: Number(id),
          workflowId: workflow?.id,
        },
      })
      handleResult(result, () => {
        throw new Error('Publish failed')
      })
      setPublishTypeModalOpen(false)
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } catch {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  return (
    <ResponsivePage.Body
      authorized={isCustomFormsEnabled || isWorkflowsEnabled}
      error={error}
      loading={loading}
      showHeader={false}
      variant="fullscreen"
    >
      {/* Modals */}
      {showPreviewModal && requestType && form && (
        <NewPurchaseRequestModal
          currencies={currencies}
          defaultCurrency={userCurrency}
          hasCustomFormsEnabled={isCustomFormsEnabled}
          isPreviewMode
          onClose={() => setShowPreviewModal(false)}
          procurementType={generatePreview(requestType, form)}
        />
      )}
      {publishTypeModalOpen && (
        <PublishTypeModal
          onClose={() => setPublishTypeModalOpen(false)}
          onPublish={handlePublish}
          onPublishLoading={publishFormLoading}
          publishingForm={!isLastFormVersionPublished}
          publishingWorkflow={!isLastWorkflowVersionPublished}
        />
      )}

      {saveFormModalOpen && (
        <SaveRequestTypeModal
          onClose={() => setSaveFormModalOpen(false)}
          onSave={handleSave}
          onSaveLoading={saveFormLoading}
          savingForm={hasFormChanges}
          savingWorkflow={hasWorkflowChanges}
        />
      )}

      {/** Header */}
      {requestType && (
        <RequestTypeDetailHeader
          hasLocalChanges={hasFormChanges || hasWorkflowChanges}
          isPublished={requestType.enabled ?? false}
          shouldPublish={isLastFormVersionPublished && isLastWorkflowVersionPublished}
          title={requestType.name}
          onPreviewForm={() => setShowPreviewModal(true)}
          onPublish={() => setPublishTypeModalOpen(true)}
          onSave={() => setSaveFormModalOpen(true)}
        />
      )}

      {/* Tab content */}
      <TabContent
        form={form}
        setForm={setForm}
        id={id}
        isCustomFormsEnabled={isCustomFormsEnabled}
        isWorkflowsEnabled={isWorkflowsEnabled}
        requestType={requestType}
        workflow={workflow}
      />
    </ResponsivePage.Body>
  )
}

// Container component that provides WorkflowProvider context
const RequestTypeDetailPage = () => {
  return (
    <WorkflowProvider>
      <RequestTypeDetailPageContent />
    </WorkflowProvider>
  )
}

export default RequestTypeDetailPage
